import { create } from 'zustand';
import type { ProjectPermissions } from '../api/projects';

export interface Project {
  id: string;
  uuid?: string;
  workspaceUuid?: string;
  title: string;
  subtitle: string;
  /** All selected project types (multi-select); `subtitle` mirrors the first entry for single-type displays. */
  projectTypes: string[];
  /** User-entered label when projectTypes includes 'Custom'; empty for predefined types. */
  customProjectType?: string;
  status: 'Active' | 'Planning' | 'Completed';
  startDate: string;
  endDate: string;
  progress: number;
  crewCount: number;
  taskCount: number;
  completedTaskCount: number;
  allocatedBudget: number;
  spentBudget: number;
  bgImage: string;
  description: string;
  linkedEvent?: string;
  linkedEventUuid?: string | null;
  isMyProject: boolean;
  isShared: boolean;
  isArchived: boolean;
  showBudgetToCollaborators: boolean;
  /** Best-effort user IDs extracted from the API's opaque `collaborators` list. */
  collaboratorIds: number[];
  /** Raw ISO (YYYY-MM-DD) dates from the API, kept alongside the human-readable startDate/endDate for date inputs. */
  rawStartDate?: string | null;
  rawEndDate?: string | null;
  /** Current user's capability flags for this project, as returned by the API — the source of truth for permission-gated UI. */
  permissions?: ProjectPermissions;
  workspaceRole?: string | null;
  projectRole?: string | null;
}

export interface Task {
  id: string;
  projectId: string;
  title: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'To Do' | 'In Progress' | 'Complete';
  assignee: {
    name: string;
    avatar?: string;
    initials: string;
    color: string;
  };
  dueDate: string;
  alerts?: ('Overdue' | 'Due Soon')[];
  description?: string;
  externalLink?: string;
  coverImage?: string;
  attachments?: string[];
}

export interface Milestone {
  id: string;
  projectId: string;
  title: string;
  dueDate: string;
  status: 'In Progress' | 'Upcoming' | 'Complete';
}

export interface BudgetCategory {
  id: string;
  projectId: string;
  name: string;
  allocated: number;
  spent: number;
  dotColor: string;
  notes?: string;
}

export interface Transaction {
  id: string;
  projectId: string;
  type: 'Expense' | 'Revenue';
  title: string;
  category: string;
  amount: number;
  date: string;
  status: 'Paid' | 'Projected' | 'Pending';
}

export interface CrewMember {
  id: string;
  projectId: string;
  name: string;
  role: string;
  status: 'Active' | 'Pending';
  description: string;
  initials: string;
  color: string;
  inviteSentDate?: string;
}

export interface Activity {
  id: string;
  projectId: string;
  user: string;
  action: string;
  timestamp: string;
  type: 'task' | 'invite' | 'budget' | 'milestone' | 'create';
  status?: string;
}

export interface InventoryItem {
  id: string;
  projectId: string;
  name: string;
  category: string;
  quantity: number;
  status: 'Available' | 'In Use' | 'Out of Stock';
  assetTag?: string;
  budgetAllocation?: string;
  location?: string;
  condition?: 'Excellent' | 'Good' | 'Fair' | 'Needs Repair';
}

export interface TicketTransaction {
  id: string;
  projectId: string;
  customerName: string;
  customerEmail: string;
  ticketType: 'Regular' | 'VIP';
  paymentMethod: 'Stripe' | 'Free booking';
  amount: number;
  quantity: number;
  dateTime: string;
}

interface ProjectsState {
  tasks: Task[];
  milestones: Milestone[];
  budgetCategories: BudgetCategory[];
  transactions: Transaction[];
  crewMembers: CrewMember[];
  activities: Activity[];
  inventoryItems: InventoryItem[];
  ticketTransactions: TicketTransaction[];

  // Actions
  addTask: (task: Omit<Task, 'id'>) => void;
  toggleTaskStatus: (taskId: string) => void;
  deleteTask: (taskId: string) => void;
  addBudgetCategory: (category: Omit<BudgetCategory, 'id' | 'spent'>) => void;
  updateBudgetCategory: (categoryId: string, updates: Partial<BudgetCategory>) => void;
  addTransaction: (transaction: Omit<Transaction, 'id'>) => void;
  inviteCollaborator: (member: Omit<CrewMember, 'id' | 'status'>) => void;
  resendInvitation: (memberId: string) => void;
  removeCollaborator: (memberId: string) => void;

  addInventoryItem: (item: Omit<InventoryItem, 'id'>) => void;
  updateInventoryItem: (itemId: string, updates: Partial<InventoryItem>) => void;
  deleteInventoryItem: (itemId: string) => void;
  addTicketTransaction: (tx: Omit<TicketTransaction, 'id'>) => void;
}

export const useProjectsStore = create<ProjectsState>((set) => ({
  tasks: [
    {
      id: 'task-1',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Confirm Lighting Vendor',
      priority: 'High',
      status: 'In Progress',
      assignee: { name: 'Lucas Smith', initials: 'LS', color: 'bg-emerald-600' },
      dueDate: 'Jul 10',
    },
    {
      id: 'task-2',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Book Hotel Accommodations',
      priority: 'Medium',
      status: 'To Do',
      assignee: { name: 'Ava Brown', initials: 'AB', color: 'bg-rose-500' },
      dueDate: 'Jun 11',
      alerts: ['Overdue'],
    },
    {
      id: 'task-3',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Book Hotel Accommodations',
      priority: 'Medium',
      status: 'To Do',
      assignee: { name: 'Ava Brown', initials: 'AB', color: 'bg-rose-500' },
      dueDate: 'Jul 1',
    },
    {
      id: 'task-4',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Finalize Stage Design',
      priority: 'High',
      status: 'Complete',
      assignee: { name: 'Lucas Smith', initials: 'LS', color: 'bg-emerald-600' },
      dueDate: 'Jun 20',
    },
    {
      id: 'task-5',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Set Up Check-in App',
      priority: 'Medium',
      status: 'In Progress',
      assignee: { name: 'Asheera Johnson', initials: 'AJ', color: 'bg-blue-600' },
      dueDate: 'Jun 15',
      alerts: ['Due Soon'],
    },
    {
      id: 'task-6',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Set Up Check-in App',
      priority: 'Medium',
      status: 'In Progress',
      assignee: { name: 'Asheera Johnson', initials: 'AJ', color: 'bg-blue-600' },
      dueDate: 'Jul 5',
    },
    {
      id: 'task-7',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Arrange Artist Transportation',
      priority: 'Medium',
      status: 'To Do',
      assignee: { name: 'Ava Brown', initials: 'AB', color: 'bg-rose-500' },
      dueDate: 'Jul 12',
    },
  ],
  milestones: [
    {
      id: 'm-1',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Venue Confirmed',
      dueDate: 'May 15',
      status: 'Complete',
    },
    {
      id: 'm-2',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Artist Contracts Signed',
      dueDate: 'Jun 5',
      status: 'Complete',
    },
    {
      id: 'm-3',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Marketing Launch',
      dueDate: 'Jul 1',
      status: 'In Progress',
    },
    {
      id: 'm-4',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Tech Rehearsal',
      dueDate: 'Jul 10',
      status: 'Upcoming',
    },
    {
      id: 'm-5',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      title: 'Event Day',
      dueDate: 'Jul 18',
      status: 'Upcoming',
    },
  ],
  budgetCategories: [
    {
      id: 'bc-1',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Venue',
      allocated: 15000,
      spent: 12500,
      dotColor: '#3B82F6',
    },
    {
      id: 'bc-2',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Talent & Performers',
      allocated: 12000,
      spent: 8000,
      dotColor: '#A78BFA',
    },
    {
      id: 'bc-3',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Production',
      allocated: 10000,
      spent: 8750,
      dotColor: '#34D399',
    },
    {
      id: 'bc-4',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Marketing',
      allocated: 7500,
      spent: 2800,
      dotColor: '#F59E0B',
    },
    {
      id: 'bc-5',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Security',
      allocated: 3000,
      spent: 1900,
      dotColor: '#EF4444',
    },
    {
      id: 'bc-6',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Contingency',
      allocated: 2500,
      spent: 0,
      dotColor: '#22D3EE',
    },
  ],
  transactions: [
    {
      id: 'tx-1',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Expense',
      title: 'Main Stage Deposit',
      category: 'Venue',
      amount: 4500,
      date: 'Jun 2, 2026',
      status: 'Paid',
    },
    {
      id: 'tx-2',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Expense',
      title: 'Digital Marketing Campaign',
      category: 'Marketing',
      amount: 1200,
      date: 'May 30, 2026',
      status: 'Paid',
    },
    {
      id: 'tx-3',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Expense',
      title: 'Artist Travel Deposit',
      category: 'Talent & Performers',
      amount: 2800,
      date: 'May 28, 2026',
      status: 'Paid',
    },
    {
      id: 'tx-4',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Expense',
      title: 'Security Services',
      category: 'Security',
      amount: 950,
      date: 'May 25, 2026',
      status: 'Paid',
    },
    {
      id: 'tx-5',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Expense',
      title: 'VIP Wristbands',
      category: 'Production',
      amount: 420,
      date: 'May 24, 2026',
      status: 'Paid',
    },

    {
      id: 'tx-6',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Revenue',
      title: 'Ticket Sales — General Admission',
      category: 'Ticket Sales',
      amount: 28000,
      date: 'Jun 1, 2026',
      status: 'Paid',
    },
    {
      id: 'tx-7',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Revenue',
      title: 'VIP Package Sales',
      category: 'VIP',
      amount: 8500,
      date: 'Jun 1, 2026',
      status: 'Paid',
    },
    {
      id: 'tx-8',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Revenue',
      title: 'Absolut Vodka Sponsorship',
      category: 'Sponsorship',
      amount: 12000,
      date: 'May 20, 2026',
      status: 'Paid',
    },
    {
      id: 'tx-9',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Revenue',
      title: 'Merchandise Pre-orders',
      category: 'Merchandise',
      amount: 1800,
      date: 'Jun 5, 2026',
      status: 'Paid',
    },
    {
      id: 'tx-10',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      type: 'Revenue',
      title: 'Bar & Beverage Revenue',
      category: 'Bar Revenue',
      amount: 4200,
      date: 'Projected Jul 18',
      status: 'Projected',
    },
  ],
  crewMembers: [
    {
      id: 'cm-1',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Asheera Johnson',
      role: 'Project Owner',
      status: 'Active',
      description: 'Full access to project settings, budget, and collaborators',
      initials: 'AJ',
      color: 'bg-blue-600',
    },
    {
      id: 'cm-2',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Emma Johnson',
      role: 'Event Producer',
      status: 'Active',
      description: 'Manages vendors, schedules, and event operations',
      initials: 'EJ',
      color: 'bg-purple-600',
    },
    {
      id: 'cm-3',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Michael Lee',
      role: 'Finance Manager',
      status: 'Active',
      description: 'Tracks expenses, invoices, and budget approvals',
      initials: 'ML',
      color: 'bg-amber-500',
    },
    {
      id: 'cm-4',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Tunde Bello',
      role: 'Stage Crew Lead',
      status: 'Pending',
      description: 'Leads physical stage setup, soundchecks and teardown',
      initials: 'TB',
      color: 'bg-zinc-700',
      inviteSentDate: 'Invite sent',
    },
    {
      id: 'cm-5',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Amara Diallo',
      role: 'Front of House',
      status: 'Pending',
      description: 'Manages ticketing lines, check-ins, and door staff',
      initials: 'AD',
      color: 'bg-zinc-700',
      inviteSentDate: 'Invite sent',
    },
  ],
  activities: [
    {
      id: 'act-1',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      user: 'Lucas Smith',
      action: 'completed task "Finalize Stage Design"',
      timestamp: '2 hours ago',
      type: 'task',
    },
    {
      id: 'act-2',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      user: 'Asheera Johnson',
      action: 'invited Ava Brown as Talent Coordinator',
      timestamp: '5 hours ago',
      type: 'invite',
    },
    {
      id: 'act-3',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      user: 'Michael Lee',
      action: 'Budget updated: $4,200 added for Sound Equipment',
      timestamp: 'Yesterday',
      type: 'budget',
    },
    {
      id: 'act-4',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      user: 'Emma Johnson',
      action: 'marked milestone "Artist Contracts Signed" as complete',
      timestamp: 'Jun 5, 2026',
      type: 'milestone',
    },
    {
      id: 'act-5',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      user: 'Asheera Johnson',
      action: 'marked milestone "Venue Confirmed" as complete',
      timestamp: 'May 15, 2026',
      type: 'milestone',
    },
    {
      id: 'act-6',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      user: 'Asheera Johnson',
      action: 'created this project',
      timestamp: 'Apr 12, 2026',
      type: 'create',
    },
  ],
  inventoryItems: [
    {
      id: 'inv-1',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Main PA System (JBL SRX)',
      category: 'Audio',
      quantity: 1,
      status: 'Available',
    },
    {
      id: 'inv-2',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Stage Lighting Rig',
      category: 'Lighting',
      quantity: 1,
      status: 'Available',
    },
    {
      id: 'inv-3',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Pioneer CDJ-3000 Decks',
      category: 'DJ Equipment',
      quantity: 2,
      status: 'Available',
    },
    {
      id: 'inv-4',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Sony A7S III Camera',
      category: 'Media',
      quantity: 3,
      status: 'Available',
    },
    {
      id: 'inv-5',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'VIP Wristbands',
      category: 'Access Control',
      quantity: 1500,
      status: 'Available',
    },
    {
      id: 'inv-6',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Wireless Microphones (Shure)',
      category: 'Audio',
      quantity: 6,
      status: 'Available',
    },
    {
      id: 'inv-7',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Security Scanners',
      category: 'Security',
      quantity: 4,
      status: 'Available',
    },
    {
      id: 'inv-8',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Crowd Barriers',
      category: 'Safety',
      quantity: 20,
      status: 'Available',
    },
    {
      id: 'inv-9',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      name: 'Stage Monitor Speakers',
      category: 'Audio',
      quantity: 8,
      status: 'Available',
    },
  ],
  ticketTransactions: [
    {
      id: 'tk-1',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Kofi Osei',
      customerEmail: 'kofi.osei@outlook.com',
      ticketType: 'VIP',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '18 Jun 2026, 17:32',
    },
    {
      id: 'tk-2',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Tobi Akinyele',
      customerEmail: 'tobi.aki.key@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Stripe',
      amount: 140,
      quantity: 1,
      dateTime: '18 Jun 2026, 09:45',
    },
    {
      id: 'tk-3',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Biodun Falola',
      customerEmail: 'biodun.falola@yahoo.com',
      ticketType: 'VIP',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '17 Jun 2026, 16:20',
    },
    {
      id: 'tk-4',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Precious Okeke',
      customerEmail: 'precious.okeke@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 140,
      quantity: 1,
      dateTime: '17 Jun 2026, 12:04',
    },
    {
      id: 'tk-5',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Oluwaseun Adebisi',
      customerEmail: 'seun.adebisi@outlook.com',
      ticketType: 'Regular',
      paymentMethod: 'Stripe',
      amount: 140,
      quantity: 1,
      dateTime: '16 Jun 2026, 14:15',
    },
    {
      id: 'tk-6',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Sean Kofi',
      customerEmail: 'sean.kofi@outlook.com',
      ticketType: 'VIP',
      paymentMethod: 'Stripe',
      amount: 350,
      quantity: 1,
      dateTime: '15 Jun 2026, 10:12',
    },
    {
      id: 'tk-7',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Zainab Ibrahim',
      customerEmail: 'zainab.ibrahim@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '14 Jun 2026, 17:05',
    },
    {
      id: 'tk-8',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Tamar Chinyelu',
      customerEmail: 'tamar.chinyelu@gmail.com',
      ticketType: 'VIP',
      paymentMethod: 'Stripe',
      amount: 350,
      quantity: 1,
      dateTime: '14 Jun 2026, 09:45',
    },
    {
      id: 'tk-9',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Mbaka Ofori',
      customerEmail: 'mbaka.ofori@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Stripe',
      amount: 140,
      quantity: 1,
      dateTime: '13 Jun 2026, 14:12',
    },
    {
      id: 'tk-10',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Bala Luzanga',
      customerEmail: 'balaluzanga@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '12 Jun 2026, 08:30',
    },
    {
      id: 'tk-11',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Mariam Yusuf',
      customerEmail: 'mariam.yusuf@yahoo.com',
      ticketType: 'VIP',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '12 Jun 2026, 06:15',
    },
    {
      id: 'tk-12',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Chukwudi Nwafor',
      customerEmail: 'chukwudi.n@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Stripe',
      amount: 140,
      quantity: 1,
      dateTime: '11 Jun 2026, 21:23',
    },
    {
      id: 'tk-13',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Yehinde Lawal',
      customerEmail: 'yehinde.lawal@outlook.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '11 Jun 2026, 10:15',
    },
    {
      id: 'tk-14',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Kemisola Oza',
      customerEmail: 'kemisola.oza@gmail.com',
      ticketType: 'VIP',
      paymentMethod: 'Stripe',
      amount: 350,
      quantity: 1,
      dateTime: '10 Jun 2026, 09:12',
    },
    {
      id: 'tk-15',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Aisha Bello',
      customerEmail: 'aisha.b@outlook.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '09 Jun 2026, 15:43',
    },
    {
      id: 'tk-16',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Karim Ekwueme',
      customerEmail: 'karim.ekw@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Stripe',
      amount: 140,
      quantity: 1,
      dateTime: '09 Jun 2026, 09:11',
    },
    {
      id: 'tk-17',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Bisi Coker',
      customerEmail: 'bisi.coker@gmail.com',
      ticketType: 'VIP',
      paymentMethod: 'Stripe',
      amount: 350,
      quantity: 1,
      dateTime: '08 Jun 2026, 11:22',
    },
    {
      id: 'tk-18',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Femi Adebayo',
      customerEmail: 'femi.adebayo@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '08 Jun 2026, 08:34',
    },
    {
      id: 'tk-19',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Hauwa Musa',
      customerEmail: 'hauwa.m@outlook.com',
      ticketType: 'VIP',
      paymentMethod: 'Stripe',
      amount: 350,
      quantity: 1,
      dateTime: '07 Jun 2026, 10:44',
    },
    {
      id: 'tk-20',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Tunde Balogun',
      customerEmail: 'tundebalogun@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '06 Jun 2026, 09:30',
    },
    {
      id: 'tk-21',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Amebo Okonkwo',
      customerEmail: 'amebo.okonkwo@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '05 Jun 2026, 14:22',
    },
    {
      id: 'tk-22',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Ngozi Obi',
      customerEmail: 'ngozi.obi@gmail.com',
      ticketType: 'VIP',
      paymentMethod: 'Stripe',
      amount: 350,
      quantity: 1,
      dateTime: '04 Jun 2026, 13:11',
    },
    {
      id: 'tk-23',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Musa Ogi',
      customerEmail: 'musa.ogi@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Stripe',
      amount: 140,
      quantity: 1,
      dateTime: '03 Jun 2026, 12:12',
    },
    {
      id: 'tk-24',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Kofo Adeyemi',
      customerEmail: 'kofo.adeyemi@yahoo.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '02 Jun 2026, 09:15',
    },
    {
      id: 'tk-25',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Fatimo Awolalu',
      customerEmail: 'fatimo.awolalu@gmail.com',
      ticketType: 'VIP',
      paymentMethod: 'Stripe',
      amount: 350,
      quantity: 1,
      dateTime: '02 Jun 2026, 07:43',
    },
    {
      id: 'tk-26',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Emeka Nwosu',
      customerEmail: 'emeka.nwosu@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Free booking',
      amount: 0,
      quantity: 1,
      dateTime: '01 Jun 2026, 11:32',
    },
    {
      id: 'tk-27',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Chinwe Eze',
      customerEmail: 'chinwe.eze@outlook.com',
      ticketType: 'VIP',
      paymentMethod: 'Stripe',
      amount: 350,
      quantity: 1,
      dateTime: '01 Jun 2026, 10:12',
    },
    {
      id: 'tk-28',
      projectId: '7987246c-9af5-489b-be85-d40a54e1681e',
      customerName: 'Adeboye Diallo',
      customerEmail: 'adeboye.diallo@gmail.com',
      ticketType: 'Regular',
      paymentMethod: 'Stripe',
      amount: 140,
      quantity: 1,
      dateTime: '01 Jun 2026, 08:34',
    },
  ],

  addTask: (task) =>
    set((state) => {
      const newId = `task-${Date.now()}`;
      const newTask: Task = { ...task, id: newId };
      return { tasks: [...state.tasks, newTask] };
    }),

  toggleTaskStatus: (taskId) =>
    set((state) => {
      const task = state.tasks.find((t) => t.id === taskId);
      if (!task) return {};

      const nextStatusMap: Record<string, 'To Do' | 'In Progress' | 'Complete'> = {
        'To Do': 'In Progress',
        'In Progress': 'Complete',
        Complete: 'To Do',
      };

      const updatedTasks = state.tasks.map((t) =>
        t.id === taskId ? { ...t, status: nextStatusMap[t.status] } : t,
      );

      return { tasks: updatedTasks };
    }),

  deleteTask: (taskId) =>
    set((state) => {
      const updatedTasks = state.tasks.filter((t) => t.id !== taskId);
      return { tasks: updatedTasks };
    }),

  addBudgetCategory: (category) =>
    set((state) => {
      const newId = `bc-${Date.now()}`;
      return {
        budgetCategories: [...state.budgetCategories, { ...category, id: newId, spent: 0 }],
      };
    }),

  updateBudgetCategory: (categoryId, updates) =>
    set((state) => ({
      budgetCategories: state.budgetCategories.map((c) =>
        c.id === categoryId ? { ...c, ...updates } : c,
      ),
    })),

  addTransaction: (transaction) =>
    set((state) => {
      const newId = `tx-${Date.now()}`;
      const updatedTrans = [...state.transactions, { ...transaction, id: newId }];

      // Update budget category spent amount if it's an expense
      let updatedCategories = state.budgetCategories;
      if (transaction.type === 'Expense') {
        updatedCategories = state.budgetCategories.map((c) => {
          if (c.projectId === transaction.projectId && c.name === transaction.category) {
            return { ...c, spent: c.spent + transaction.amount };
          }
          return c;
        });
      }

      return {
        transactions: updatedTrans,
        budgetCategories: updatedCategories,
      };
    }),

  inviteCollaborator: (member) =>
    set((state) => {
      const newId = `cm-${Date.now()}`;
      const newMember: CrewMember = {
        ...member,
        id: newId,
        status: 'Pending',
        inviteSentDate: 'Invite sent',
      };

      const updatedCrew = [...state.crewMembers, newMember];

      // Add activity log
      const newAct: Activity = {
        id: `act-${Date.now()}`,
        projectId: member.projectId,
        user: 'Asheera Johnson',
        action: `invited ${member.name} as ${member.role}`,
        timestamp: 'Just now',
        type: 'invite',
      };

      return {
        crewMembers: updatedCrew,
        activities: [newAct, ...state.activities],
      };
    }),

  resendInvitation: (memberId) =>
    set((state) => {
      return {
        crewMembers: state.crewMembers.map((c) =>
          c.id === memberId ? { ...c, inviteSentDate: 'Invite sent (Resent)' } : c,
        ),
      };
    }),

  removeCollaborator: (memberId) =>
    set((state) => {
      const member = state.crewMembers.find((c) => c.id === memberId);
      if (!member) return {};

      const updatedCrew = state.crewMembers.filter((c) => c.id !== memberId);

      return {
        crewMembers: updatedCrew,
      };
    }),

  addInventoryItem: (item) =>
    set((state) => {
      const newId = `inv-${Date.now()}`;
      return { inventoryItems: [...state.inventoryItems, { ...item, id: newId }] };
    }),

  updateInventoryItem: (itemId, updates) =>
    set((state) => ({
      inventoryItems: state.inventoryItems.map((i) => (i.id === itemId ? { ...i, ...updates } : i)),
    })),

  deleteInventoryItem: (itemId) =>
    set((state) => {
      return { inventoryItems: state.inventoryItems.filter((i) => i.id !== itemId) };
    }),

  addTicketTransaction: (tx) =>
    set((state) => {
      const newId = `tk-${Date.now()}`;
      return { ticketTransactions: [...state.ticketTransactions, { ...tx, id: newId }] };
    }),
}));
